import type { CalendarTimelineDay, CalendarTimelineItem } from '@/api/schema/calendar'
import { dayjs } from '@/api/utils/dayjs'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Link } from '@/app/components/Link/Link'
import { MoonIcon } from '@/app/components/MoonIcon/MoonIcon'
import { Motion } from '@/app/components/Motion/Motion'
import { Spinner } from '@/app/components/Spinner/Spinner'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import ChevronLeft from '@/app/icons/chevron-left.svg?react'
import ChevronRight from '@/app/icons/chevron-right.svg?react'
import { getMoonEventLink } from '@/app/utils/moon'
import { isCalendarMonthAllowed } from '@/shared/calendar/date-range'
import classNames from 'classnames'
import type { FunctionalComponent } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import styles from './index.module.scss'

export const Timeline: FunctionalComponent<{
  events: CalendarTimelineDay[]
  month: number
  year: number
  currentDate: string
  timezone: string
  loading?: boolean
}> = props => {
  const [showAllDays, setShowAllDays] = useState(false)
  const shouldAnimateTimeline = useRef(false)
  const displayedMonthDate = props.events[0]?.date

  useEffect(() => {
    setShowAllDays(false)
  }, [displayedMonthDate])

  useEffect(() => {
    shouldAnimateTimeline.current = true
  }, [])

  if (!props.events.length) {
    return null
  }

  const currentMonth = dayjs()
    .year(props.year)
    .month(props.month - 1)
    .startOf('month')
  const displayedMonth = dayjs(props.events[0].date).startOf('month')

  const isSelectedCurrentMonth = currentMonth.format('YYYY-MM') === props.currentDate.slice(0, 7)
  const isDisplayedCurrentMonth = displayedMonth.format('YYYY-MM') === props.currentDate.slice(0, 7)
  const hasHiddenDays = isDisplayedCurrentMonth && props.events.some(day => day.date < props.currentDate)
  const isLoading = props.loading || !currentMonth.isSame(displayedMonth, 'month')

  const previousMonth = currentMonth.subtract(1, 'month')
  const nextMonth = currentMonth.add(1, 'month')
  const now = dayjs(props.currentDate)

  return (
    <UI.Panel padding='0'>
      <UI.Panel
        type='ghost'
        class={styles.panel}
      >
        <Col
          alignItems='center'
          justifyContent='space-between'
        >
          <Col
            gap={5}
            alignItems='center'
          >
            <Text
              upper
              as='h2'
              fontSize={14}
              fontSizeMobile={13}
              fontWeight={600}
              lineHeight={1}
            >
              {currentMonth.format('MMMM YYYY')}
            </Text>

            {isLoading && <Spinner size={13} />}
          </Col>

          <Col
            gap={5}
            class={styles.nav}
            alignItems='center'
          >
            {!isSelectedCurrentMonth && (
              <UI.Button
                replace
                size='xs'
                height={26}
                type='secondary'
                link='/moon'
              >
                Сегодня
              </UI.Button>
            )}

            <Col>
              <UI.Button
                replace
                square
                size='xs'
                height={32}
                type='light'
                disabled={!isCalendarMonthAllowed(previousMonth, now)}
                link={isCalendarMonthAllowed(previousMonth, now)
                  ? getTimelineLink(previousMonth)
                  : undefined}
              >
                <ChevronLeft
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                />
              </UI.Button>

              <UI.Button
                replace
                square
                size='xs'
                height={32}
                type='light'
                disabled={!isCalendarMonthAllowed(nextMonth, now)}
                link={isCalendarMonthAllowed(nextMonth, now)
                  ? getTimelineLink(nextMonth)
                  : undefined}
              >
                <ChevronRight
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                />
              </UI.Button>
            </Col>
          </Col>
        </Col>
      </UI.Panel>

      <Motion
        key={displayedMonthDate}
        initialX={shouldAnimateTimeline.current ? undefined : 0}
        initialO={shouldAnimateTimeline.current ? undefined : 1}
      >
        <div
          class={classNames(styles.list, {
            [styles.loading]: isLoading,
          })}
        >
          {hasHiddenDays && !showAllDays && (
            <button
              type='button'
              class={classNames(styles.row, styles.expand)}
              onClick={() => setShowAllDays(true)}
            >
              <UI.Panel type='holder'>
                {getShowAllDaysLabel(props.currentDate)}
              </UI.Panel>
            </button>
          )}

          {props.events.map(day => (
            <div
              key={day.date}
              hidden={hasHiddenDays && !showAllDays && day.date < props.currentDate}
              class={classNames(styles.row, {
                [styles.active]: day.date === props.currentDate,
              })}
            >
              <UI.Panel type='ghost'>
                <Row gap={10}>
                  <Col alignItems='center'>
                    <Col
                      gap={5}
                      alignItems='center'
                      class={styles.day}
                    >
                      <MoonIcon
                        phase={day.moon.id}
                      />

                      <Text
                        upper
                        fontSize={13}
                        fontSizeMobile={12}
                        fontWeight={600}
                      >
                        {dayjs(day.date).format('dd')}
                      </Text>
                    </Col>

                    <Text
                      upper
                      fontSize={13}
                      fontSizeMobile={12}
                      fontWeight={600}
                    >
                      {dayjs(day.date).format('D MMM').replace('.', '')}
                    </Text>
                  </Col>

                  <Row class={styles.eventList}>
                    {day.events.map(event => (
                      <Link
                        to={getEventLink(event)}
                        key={`${event.type}-${event.at}`}
                        class={classNames(styles.eventRow, {
                          [styles.accent]: event.type === 'moon_phase',
                        })}
                      >
                        <Col alignItems='baseline'>
                          <Text
                            fontSize={13}
                            opacity={event.type !== 'moon_phase' ? 0.5 : undefined}
                            class={styles.time}
                            fontFamily='PT Mono'
                          >
                            {dayjs(event.at).tz(props.timezone).format('HH:mm')}
                          </Text>

                          <Text
                            fontSize={14}
                            class={styles.eventName}
                          >
                            {event.title}
                          </Text>
                        </Col>
                      </Link>
                    ))}
                  </Row>
                </Row>
              </UI.Panel>
            </div>
          ))}
        </div>
      </Motion>
    </UI.Panel>
  )
}

function getTimelineLink(month: ReturnType<typeof dayjs>) {
  return `/moon?month=${month.month() + 1}&year=${month.year()}`
}

function getShowAllDaysLabel(currentDate: string) {
  const previousDay = dayjs(currentDate).subtract(1, 'day')
  const previousDayLabel = previousDay.format('D MMM').replace('.', '')

  return previousDay.date() === 1
    ? `Показать ${previousDayLabel}`
    : `Показать 1–${previousDayLabel}`
}

function getEventLink(event: CalendarTimelineItem) {
  switch (event.type) {
    case 'moon':
      return getMoonEventLink(`~/moon/sign/${event.sign.toLowerCase()}`, event.at)
    case 'lunar_day':
      return getMoonEventLink(`~/moon/day/${event.lunarDay}`, event.at)
    case 'moon_phase':
      return getMoonEventLink(
        event.phase.id === 'full_moon'
          ? `~/moon/full-moon/${event.sign.toLowerCase()}`
          : `~/moon/new-moon/${event.sign.toLowerCase()}`,
        event.at,
      )
  }
}
